import * as Sentry from '@sentry/browser';

// Frontend Sentry client. Everything it needs is passed from Rails via meta tags in <head>
// (rendered only when ApplicationHelper#sentry_frontend_enabled? -- see
// app/views/layouts/application.html.slim) rather than baked into the Vite build, since it
// varies per deployment environment. Imported first in entrypoints/application.js so it
// captures errors from the moment the app loads.
const metaContent = (name: string) =>
  document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') ??
  undefined;

const dsn = metaContent('sentry-public-dsn');
// Mirrors the backend's config.environment (see config/initializers/sentry.rb /
// ApplicationHelper#sentry_environment_meta_tag) so alpha/beta/prod stay distinguishable
// in Sentry even when SENTRY_PUBLIC_DSN is shared across deployments.
const environment = metaContent('sentry-environment');

// The signed-in user's traits (ApplicationHelper#sentry_user_meta_tag). Read at init rather
// than pushed by an inline script: this module is deferred, so any inline body script runs
// before it and would find no client to talk to (ENG-8094).
const parseUser = (): Record<string, unknown> | undefined => {
  const raw = metaContent('sentry-user');
  if (!raw) return undefined;

  try {
    const user: unknown = JSON.parse(raw);
    return user && typeof user === 'object'
      ? (user as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
};

// SENTRY_FRONTEND_SAMPLE_RATE, validated again here so a bad value never disables capture.
const parseSampleRate = () => {
  const rate = Number.parseFloat(metaContent('sentry-sample-rate') ?? '');
  return rate >= 0 && rate <= 1 ? rate : 1;
};

// Volume guards, so a noisy page cannot exhaust the Sentry quota. Tracing and replay are
// deliberately not enabled: only error events are sent.
const MAX_EVENTS_PER_PAGE = 10;

const IGNORE_ERRORS = [
  // Benign browser layout notices, reported as errors by Chrome and Firefox respectively
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
];

// Errors whose stack frames come from code we do not own
const DENY_URLS = [
  /^(chrome|moz|safari|safari-web)-extension:\/\//i,
  /cdn\.segment\.com/i,
  /analytics\.churnzero\.net/i,
];

if (dsn) {
  const user = parseUser();
  let sentEvents = 0;

  Sentry.init({
    // Runs after sampling, so the cap counts events that would actually be sent
    beforeSend: (event) => {
      sentEvents += 1;
      return sentEvents > MAX_EVENTS_PER_PAGE ? null : event;
    },
    denyUrls: DENY_URLS,
    dsn,
    environment,
    ignoreErrors: IGNORE_ERRORS,
    initialScope: user ? { user } : undefined,
    sampleRate: parseSampleRate(),
  });
}

// The asset-pipeline bundle (app/assets/javascripts, compiled separately by Sprockets)
// cannot import from this entrypoint, so the pieces it needs are bridged onto window.
// Assigned unconditionally: each method no-ops without a DSN, so callers guarding on
// `window.EmSentry` are guarding against load order, not against an unconfigured DSN.
declare global {
  interface Window {
    EmSentry?: {
      captureException: (error: unknown) => void;
    };
  }
}

window.EmSentry = {
  captureException: (error) => {
    if (dsn) {
      Sentry.captureException(error);
    }
  },
};
